import { useEffect, useState } from 'react';
import { formatDateTime, formatRelativeTime } from '../utils/relativeTime';

export interface RelativeTimeLabel {
  relative: string;
  absolute: string;
}

const TICK_MS = 1_000;

const listeners = new Set<() => void>();
let ticker: ReturnType<typeof setInterval> | null = null;

function subscribeToClock(listener: () => void): () => void {
  listeners.add(listener);
  if (ticker === null) {
    ticker = setInterval(() => listeners.forEach((notify) => notify()), TICK_MS);
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && ticker !== null) {
      clearInterval(ticker);
      ticker = null;
    }
  };
}

function labelFor(value: string | null | undefined): RelativeTimeLabel | null {
  const relative = formatRelativeTime(value, Date.now());
  const absolute = formatDateTime(value);
  if (relative === null || absolute === null) return null;
  return { relative, absolute };
}

function isSameLabel(a: RelativeTimeLabel | null, b: RelativeTimeLabel | null): boolean {
  if (a === null || b === null) return a === b;
  return a.relative === b.relative && a.absolute === b.absolute;
}

export function useRelativeTime(value: string | null | undefined): RelativeTimeLabel | null {
  const [label, setLabel] = useState(() => labelFor(value));

  useEffect(() => {
    const refresh = () =>
      setLabel((shown) => {
        const current = labelFor(value);
        return isSameLabel(shown, current) ? shown : current;
      });
    refresh();
    return subscribeToClock(refresh);
  }, [value]);

  return label;
}
